import React, { useEffect, useState } from 'react';
import { Save, Loader2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { SITE_TEXT_GROUPS, setSiteTextsCache } from '../lib/siteTexts';

const inputCls = 'w-full bg-brand-bg border border-white/10 focus:border-brand-primary text-white px-3 py-2 outline-none text-sm';

const Field = ({ k, label, def, multi, value, onChange }) => {
    const v = value ?? def;
    return (
    <label className="block">
        <span className="flex items-center justify-between text-xs text-white/50 mb-1">
            {label}
            {value !== undefined && value !== def && <button type="button" onClick={() => onChange(undefined)} className="inline-flex items-center gap-1 hover:text-white" data-testid={`st-reset-${k}`}><RotateCcw className="h-3 w-3" />default</button>}
        </span>
        {multi
            ? <textarea rows={Math.min(8, Math.max(2, def.split('\n').length))} value={v} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={`st-${k}`} />
            : <input value={v} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={`st-${k}`} />}
    </label>
    );
};

const SiteTextsAdmin = () => {
    const [texts, setTexts] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => { api.get('/site-texts').then((r) => setTexts(r.data || {})); }, []);
    const defaults = Object.fromEntries(SITE_TEXT_GROUPS.flatMap((g) => g.fields.map(([k, , d]) => [k, d])));
    const save = async () => {
        setBusy(true);
        try {
            const changed = Object.fromEntries(Object.entries(texts).filter(([k, v]) => v && v.trim() && v.trim() !== defaults[k]));
            const { data } = await api.put('/admin/site-texts', { texts: changed });
            setTexts(data);
            setSiteTextsCache(data);
            toast.success('Saved — live on the site now');
        } catch {
            toast.error('Could not save — please try again');
        }
        setBusy(false);
    };
    if (!texts) return <div className="text-white/50">Loading…</div>;

    return (
        <div data-testid="admin-site-texts">
            <div className="flex items-start justify-between gap-4 mb-6 sticky top-0 z-10 bg-brand-bg py-3">
                <div>
                    <h2 className="font-display font-black uppercase text-3xl mb-1">Site texts</h2>
                    <p className="text-white/50 text-sm">Edit the homepage and footer texts. Press Enter in multi-line boxes for a new line. A box left empty uses the original text. Hero headline and stats are in Branding.</p>
                </div>
                <button onClick={save} disabled={busy} className="inline-flex items-center gap-2 bg-brand-primary hover:bg-brand-primaryHover disabled:opacity-60 text-brand-ink font-bold px-5 py-3 label-caps flex-shrink-0" data-testid="st-save">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save all
                </button>
            </div>
            <div className="space-y-6">
                {SITE_TEXT_GROUPS.map((g) => (
                    <details key={g.title} className="border border-white/10 group" open={g.title.startsWith('Footer')}>
                        <summary className="cursor-pointer p-4 font-display font-bold uppercase text-white list-none flex justify-between" data-testid={`st-group-${g.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                            {g.title}<span className="text-white/40 group-open:rotate-180 transition-transform">▾</span>
                        </summary>
                        <div className="p-4 pt-0 grid grid-cols-1 md:grid-cols-2 gap-4">
                            {g.fields.map(([k, label, def, multi]) => (
                                <div key={k} className={multi ? 'md:col-span-2' : ''}>
                                    <Field k={k} label={label} def={def} multi={multi} value={texts[k]} onChange={(v) => setTexts({ ...texts, [k]: v })} />
                                </div>
                            ))}
                        </div>
                    </details>
                ))}
            </div>
        </div>
    );
};

export default SiteTextsAdmin;
